import React, { useState, useRef, useEffect, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import { Link, useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { getFallbackSEO } from '../config/seoFallback';
import { isHydratingPrerenderedDocument, isReactSnapUserAgent } from '../utils/prerender';
// Lucide icons: per-file subpath imports so webpack tree-shakes properly (barrel imports leak whole library)
import Palette from 'lucide-react/dist/esm/icons/palette';
import Bug from 'lucide-react/dist/esm/icons/bug';
import Smartphone from 'lucide-react/dist/esm/icons/smartphone';
import Code from 'lucide-react/dist/esm/icons/code';
import Database from 'lucide-react/dist/esm/icons/database';
import Shield from 'lucide-react/dist/esm/icons/shield';
import BarChart2 from 'lucide-react/dist/esm/icons/bar-chart-2';
import Gauge from 'lucide-react/dist/esm/icons/gauge';
import Lightbulb from 'lucide-react/dist/esm/icons/lightbulb';
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check';
import Settings from 'lucide-react/dist/esm/icons/settings';
import Bot from 'lucide-react/dist/esm/icons/bot';
import Link2 from 'lucide-react/dist/esm/icons/link-2';
import Cloud from 'lucide-react/dist/esm/icons/cloud';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import Code2 from 'lucide-react/dist/esm/icons/code-2';
import Atom from 'lucide-react/dist/esm/icons/atom';
import Server from 'lucide-react/dist/esm/icons/server';
import FileCode from 'lucide-react/dist/esm/icons/file-code';

import { newsletterService } from '../services/analyticsService';
import { useSectionContent, getJsonContent } from '../hooks/useCMS';
import AICityScene from '../components/AICityScene';
import dubaiFashionLogo from '../assets/client-logos/dubaifashionweek.png';
import masterLeadsLogo from '../assets/client-logos/masterleads.png';
import theOnePercentLogo from '../assets/client-logos/the1percent.png';
import menyoozeLogo from '../assets/client-logos/Menyooze.png';
import healLogo from '../assets/client-logos/Heal-private.png';

// Lazy load below-the-fold sections to shrink initial bundle and speed up first paint
const Capabilities = lazy(() => import('../components/Capabilities'));
const FlowchartSteps = lazy(() => import('../components/FlowchartSteps'));


// Icon mapping for dynamic rendering (keys match CMS icon_name; values are lucide for small bundle)
const iconMap: { [key: string]: React.ComponentType<{ style?: React.CSSProperties }> } = {
  MdDesignServices: Palette,
  MdBugReport: Bug,
  MdPhoneAndroid: Smartphone,
  MdCode: Code,
  MdStorage: Database,
  MdSecurity: Shield,
  MdAnalytics: BarChart2,
  MdSpeed: Gauge,
  HiLightBulb: Lightbulb,
  HiShieldCheck: ShieldCheck,
  TbSettings: Settings,
  TbRobot: Bot,
  TbLink: Link2,
  TbCloud: Cloud,
  TbChartLine: TrendingUp,
  TbBrandFigma: Palette,
  TbApi: Code2,
  TbBrandReact: Atom,
  TbBrandNodejs: Server,
  TbBrandPython: FileCode,
};

// Dynamic Icon Component
const DynamicIcon: React.FC<{ iconName: string; style?: React.CSSProperties }> = ({ iconName, style }) => {
  const IconComponent = iconMap[iconName] || Settings;
  return React.createElement(IconComponent, { style });
};

const gradientHighlightSx = {
  display: 'inline-block',
  fontWeight: 700,
  background: 'linear-gradient(45deg, #6366F1 30%, #EC4899 90%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent'
};

// Helper function to render highlighted text
const renderHighlightedText = (text: string) => {
  if (!text) return null;
  
  // Split text by **text** pattern and create highlighted spans
  const parts = text.split(/(\*\*.*?\*\*)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      // Remove ** markers and create highlighted span
      const highlightedText = part.slice(2, -2);
      return (
        <Box
          key={index}
          component="span"
          sx={{
            ...gradientHighlightSx
          }}
        >
          {highlightedText}
        </Box>
      );
    }
    return part;
  });
};

const NewHome: React.FC = () => {
  const navigate = useNavigate();
  const [clientPage, setClientPage] = useState(0);
  const clientsRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const reviewsSectionRef = useRef<HTMLElement>(null);
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const pendingMouseRef = useRef<{ clientX: number; clientY: number } | null>(null);
  const mouseRafIdRef = useRef<number | null>(null);

  // Add CSS animation for rotating effect
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes rotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Fetch CMS content
  // Skip CMS while react-snap is saving HTML. The first visitor render also
  // has no CMS yet, so the snapshot and hydration paint the same fallback copy.
  // The fetch still runs for real visitors after that first paint.
  const loadHomeCms = !isReactSnapUserAgent();
  const { content: heroContent } = useSectionContent('home', 'hero', loadHomeCms);
  const { content: servicesContent } = useSectionContent('home', 'services', loadHomeCms);
  const { content: clientsContent } = useSectionContent('home', 'clients', loadHomeCms);
  const { content: testimonialsContent } = useSectionContent('home', 'testimonials', loadHomeCms);
  const { content: getStartedContent } = useSectionContent('home', 'get_started', loadHomeCms);
  const { content: newsletterContent } = useSectionContent('home', 'newsletter', loadHomeCms);
  // seo fetch removed — static from seoFallback.ts
  
  // Parse CMS content
  const hero = heroContent?.[0] ? getJsonContent(heroContent[0]) : null;
  // The crawl must save the same hero the first client render paints (CMS has
  // not arrived yet). Applying the CMS headline during react-snap, and the old
  // rewrite that inserted a second "Business", froze "Business Business" into
  // the HTML and mismatched hydration. Visitors still receive the CMS headline
  // after hydrate, with Windows line endings normalised and no extra word.
  const heroView = isReactSnapUserAgent() ? null : hero;
  const heroHeadline = heroView?.headline
    ? String(heroView.headline).replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    : '';
  
  /* hero now uses inline SVG (AICityScene) — no image preload needed */
  const services = servicesContent?.[0] ? getJsonContent(servicesContent[0]) : null;
  const clients = clientsContent?.[0] ? getJsonContent(clientsContent[0]) : null;
  const testimonialsData = testimonialsContent?.[0] ? getJsonContent(testimonialsContent[0]) : null;
  const getStarted = getStartedContent?.[0] ? getJsonContent(getStartedContent[0]) : null;
  const newsletter = newsletterContent?.[0] ? getJsonContent(newsletterContent[0]) : null;
  useEffect(() => {
    // CMS content loaded
  }, [hero, services, clients, testimonialsData, getStarted, newsletter]);

  // Define client logo type
  interface ClientLogo {
    name: string;
    logo: string;
  }

  // Logo mapping for imported assets
  const logoMap: { [key: string]: string } = {
    'dubaiFashionLogo': dubaiFashionLogo,
    'masterLeadsLogo': masterLeadsLogo,
    'theOnePercentLogo': theOnePercentLogo,
    'menyoozeLogo': menyoozeLogo,
    'healLogo': healLogo
  };

  // Client logos array - Use CMS data with fallback
  const allClientLogos: ClientLogo[] = clients?.logos && clients.logos.length > 0 ? clients.logos.map((client: any) => ({
    name: client.name,
    logo: logoMap[client.logo_url] || client.logo_url // Map to imported logo or use URL directly
  })) : [
    { name: 'Dubai Fashion Week', logo: dubaiFashionLogo },
    { name: 'Master Leads', logo: masterLeadsLogo },
    { name: 'The 1%', logo: theOnePercentLogo },
    { name: 'Menyooze', logo: menyoozeLogo },
    { name: 'Heal', logo: healLogo }
  ];

  // Client logos for mobile/tablet slider (2 per page)
  const mobileLogosPerPage = 2;
  const mobileTotalPages = Math.ceil(allClientLogos.length / mobileLogosPerPage);

  // Mobile/tablet logos organized by page (2 logos per page)
  const mobileClientLogos = [];
  for (let i = 0; i < allClientLogos.length; i += mobileLogosPerPage) {
    const page = allClientLogos.slice(i, i + mobileLogosPerPage);
    mobileClientLogos.push(page);
  }
  
  // Client logos structure prepared
  


  // Function to handle client carousel navigation
  const handleClientScroll = (direction: 'next' | 'prev' | number) => {
    if (direction === 'next') {
      setClientPage(prev => (prev + 1) % mobileTotalPages);
    } else if (direction === 'prev') {
      setClientPage(prev => (prev - 1 + mobileTotalPages) % mobileTotalPages);
    } else {
      setClientPage(direction);
    }
  };



  // Define testimonial type
  interface Testimonial {
    quote: string;
    author: string;
    position: string;
  }

  // Helper function to render hero headline with highlight effects
  const renderHeroHeadline = (headline: string) => {
    const lines = headline.split('\n');
    
    return lines.map((line, lineIndex) => {
      // Words that should have highlight effect (italic + yellow underline)
      const highlightWords = ['unleashing', 'potential', 'next-level', 'ai', 'innovation', 'future', 'technology'];
      const words = line.split(' ');
      
      return (
        <React.Fragment key={lineIndex}>
          {lineIndex > 0 && <br />}
          {words.map((word, wordIndex) => {
            const cleanWord = word.toLowerCase().replace(/[^a-z-]/g, '');
            const shouldHighlight = highlightWords.includes(cleanWord);
            
            if (shouldHighlight) {
              return (
                <React.Fragment key={wordIndex}>
                  {wordIndex > 0 && ' '}
                  <Box
                    component="span"
                    sx={{
                      ...gradientHighlightSx
                    }}
                  >
                    {word}
                  </Box>
                </React.Fragment>
              );
            } else {
              return wordIndex > 0 ? ` ${word}` : word;
            }
          })}
        </React.Fragment>
      );
    });
  };

  // Use CMS testimonials with fallback
  const testimonials: Testimonial[] = testimonialsData?.items || [
    {
      quote: "Brown Tech's expertise in AI has been instrumental in our growth. Their solutions have helped us streamline operations and improve customer experiences.",
      author: "Jordan Peters",
      position: "CTO, InnovateAI"
    },
    {
      quote: "The team quickly understood our needs and delivered a solution that exceeded our expectations. Their technical knowledge and commitment to quality are impressive.",
      author: "Maria Chen",
      position: "VP of Product, BrightCo"
    },
    {
      quote: "I couldn't be happier with the results. Brown Tech delivered on time and within budget, and their ongoing support has been exceptional.",
      author: "Mark Arnold",
      position: "CEO, TechFuture"
    },
    {
      quote: "Their innovative approach to problem-solving and deep technical expertise made them the perfect partner for our digital transformation journey.",
      author: "Sarah Johnson",
      position: "CIO, FutureTech"
    },
    {
      quote: "Working with Brown Tech has been a game-changer for our business. Their AI solutions have significantly improved our operational efficiency.",
      author: "David Zhang",
      position: "COO, TechGrowth"
    },
    {
      quote: "The level of professionalism and technical excellence demonstrated by the Brown Tech team is outstanding. They consistently deliver high-quality solutions.",
      author: "Emily Brown",
      position: "Director of Engineering, InnovateCorp"
    }
  ];

  const [currentReview, setCurrentReview] = useState(0);
  const [currentMobileReviewPage, setCurrentMobileReviewPage] = useState(0);
  const reviewsPerPage = 3;
  const totalReviews = testimonials.length;
  const totalMobileReviewPages = testimonials.length; // 6 pages for mobile (1 per page)
  const [fillOrigin, setFillOrigin] = useState({ x: '50%', y: '50%' });
  
  // Newsletter popup state
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const handleButtonMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const button = e.currentTarget;
    const clientX = e.clientX;
    const clientY = e.clientY;
    requestAnimationFrame(() => {
      const rect = button.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const percentX = `${(x / rect.width) * 100}%`;
      const percentY = `${(y / rect.height) * 100}%`;
      setFillOrigin({ x: percentX, y: percentY });
    });
  };

  const handleReviewScroll = (direction: 'prev' | 'next') => {
    if (direction === 'next') {
      setCurrentReview((prev) => (prev + 1) % totalReviews);
    } else {
      setCurrentReview((prev) => (prev - 1 + totalReviews) % totalReviews);
    }
  };

  const handleMobileReviewScroll = (direction: 'prev' | 'next' | number) => {
    if (direction === 'next') {
      setCurrentMobileReviewPage((prev) => (prev + 1) % totalMobileReviewPages);
    } else if (direction === 'prev') {
      setCurrentMobileReviewPage((prev) => (prev - 1 + totalMobileReviewPages) % totalMobileReviewPages);
    } else {
      setCurrentMobileReviewPage(direction);
    }
  };

  // Newsletter popup handlers
  const handleNewsletterOpen = () => {
    setNewsletterOpen(true);
  };

  const handleNewsletterClose = () => {
    setNewsletterOpen(false);
    setEmail('');
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error'
      });
      return;
    }

    try {
      // Send the email to Laravel backend
      const response = await newsletterService.subscribe(email);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || 'Thank you for subscribing to our newsletter!',
          severity: 'success'
        });
        handleNewsletterClose();
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to subscribe. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to subscribe. Please try again later.',
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Text field styling to match contact page
  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.8)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 0, 0, 1)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'rgba(0, 0, 0, 1)',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(0, 0, 0, 0.6)',
    },
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!reviewsSectionRef.current) return;

      pendingMouseRef.current = { clientX: e.clientX, clientY: e.clientY };

      if (mouseRafIdRef.current == null) {
        mouseRafIdRef.current = requestAnimationFrame(() => {
          const el = reviewsSectionRef.current;
          const pending = pendingMouseRef.current;
          if (el && pending) {
            const rect = el.getBoundingClientRect();
            const x = pending.clientX - rect.left;
            const y = pending.clientY - rect.top;
            mousePositionRef.current = { x, y };
            setMousePosition({ x, y });
          }
          mouseRafIdRef.current = null;
        });
      }
    };

    const reviewsSection = reviewsSectionRef.current;
    if (reviewsSection) {
      reviewsSection.addEventListener('mousemove', handleMouseMove, { passive: true });
    }

    return () => {
      if (reviewsSection) {
        reviewsSection.removeEventListener('mousemove', handleMouseMove);
      }
      if (mouseRafIdRef.current != null) {
        cancelAnimationFrame(mouseRafIdRef.current);
        mouseRafIdRef.current = null;
      }
    };
  }, []);

  const calculateDistance = (elementX: number, elementY: number) => {
    const dx = mousePosition.x - elementX;
    const dy = mousePosition.y - elementY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTransform = (elementX: number, elementY: number, scale: number = 1, rotation: number = 0) => {
    const maxDistance = 300; // Interaction distance
    const distance = calculateDistance(elementX, elementY);
    
    if (distance < maxDistance) {
      // Increase force for more dramatic movement
      const force = (1 - distance / maxDistance) * 100;
      const angle = Math.atan2(mousePosition.y - elementY, mousePosition.x - elementX);
      const moveX = -Math.cos(angle) * force;
      const moveY = -Math.sin(angle) * force;
      
      // Enhanced scaling effect based on proximity
      const scaleMultiplier = 1 + (1 - distance / maxDistance) * 0.3;
      const currentScale = scale * scaleMultiplier;
      
      // Enhanced rotation based on movement direction
      const rotationAngle = rotation + (moveX * 0.2 + moveY * 0.2);
      
      return `translate(${moveX}px, ${moveY}px) scale(${currentScale}) rotate(${rotationAngle}deg)`;
    }
    return `scale(${scale}) rotate(${rotation}deg)`;
  };
  const fbSEO = getFallbackSEO('/');
  return (
    <div>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{fbSEO?.title}</title>
        <meta name="description" content={fbSEO?.description || ''} />
        {fbSEO?.keywords && <meta name="keywords" content={fbSEO.keywords} />}
        <meta property="og:title" content={fbSEO?.title || 'Brown Tech Int.'} />
        <meta property="og:description" content={fbSEO?.description || ''} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Brown Tech Int." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fbSEO?.title || 'Brown Tech Int.'} />
        <meta name="twitter:description" content={fbSEO?.description || ''} />
      </Helmet>

      {/* Hero Section */}
      <Box 
        component="section" 
        className="bg-white text-gray-900"
        sx={{ 
          pt: { xs: 15, md: 10}, 
          pb: { xs: 5, md: 10 },
          contentVisibility: 'auto',
          containIntrinsicSize: 'auto 500px'
        }}
      >
        <Container 
          maxWidth={false}
          sx={{ 
            maxWidth: '1400px', 
            px: { xs: 3, sm: 4, md: 5, lg: 6 },
            margin: '0 auto',
            textAlign: 'left'
          }}
        >
          <Grid container spacing={6}>
            <Grid item xs={12} md={6} sx={{ textAlign: 'left', md: { flex: '0 0 46%', maxWidth: '46%' } }}>
              <Box sx={{ textAlign: 'left', width: '100%' }}>
                <motion.div
                  initial={isHydratingPrerenderedDocument() ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8 }}
                  className="hero-content-mobile"
                  style={{ textAlign: 'left', width: '100%' }}
                >
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    className="font-bold mb-6"
                    style={{ textAlign: 'left' }}
                    sx={{ 
                      fontSize: { xs: '2.2rem', md: '3rem', lg: '3.5rem' },
                      lineHeight: 1.1,
                      fontWeight: 700,
                      marginBottom: '1.5rem',
                      color: '#1a202c',
                      textAlign: 'left',
                      width: '100%'
                    }}
                  >
                    {heroHeadline ? (
                      renderHeroHeadline(heroHeadline)
                    ) : (
                      // Default hardcoded headline with styling
                      <>
                        <Box
                          component="span"
                          sx={{
                            ...gradientHighlightSx,
                            lineHeight: 1.4
                          }}
                        >
                          Unleashing
                        </Box> Your<br />
                        Business <Box
                          component="span"
                        sx={{
                          ...gradientHighlightSx
                        }}
                        >
                          Potential
                        </Box><br />
                        with <Box
                          component="span"
                        sx={{
                          ...gradientHighlightSx
                        }}
                        >
                          Next-Level
                        </Box><br />
                        <Box
                          component="span"
                        sx={{
                          ...gradientHighlightSx
                        }}
                        >
                          AI
                        </Box> Solutions
                      </>
                    )}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    className="mb-8"
                    style={{ textAlign: 'left' }}
                    sx={{ 
                      fontSize: { xs: '1rem', md: '1.125rem' },
                      lineHeight: 1.6,
                      maxWidth: '540px',
                      color: '#4a5568',
                      marginBottom: '2rem',
                      textAlign: 'left',
                      width: '100%'
                    }}
                  >
                    {heroView?.description || "We are a data-driven, nearshore software agency that values speed, performance, and scalability. That's why we consistently surpass benchmarks for client retention after 750+ product launches and 13 years in business."}
                  </Typography>
                  <Box sx={{ textAlign: 'left', width: '100%' }}>
                    <Button
                      component={Link}
                      to={heroView?.cta_link || "/contact"}
                      variant="outlined"
                      endIcon={<ArrowForwardIcon />}
                      onMouseEnter={handleButtonMouseEnter}
                      sx={{ 
                        borderColor: '#000',
                        color: '#000',
                        borderRadius: '9999px',
                        padding: '1rem',
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 500,
                        marginTop: '20px',
                        marginBottom: { xs: '2rem', sm: '1.5rem', md: 0 },
                        position: 'relative',
                        overflow: 'hidden',
                        alignSelf: 'flex-start',
                        float: 'left',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          left: `calc(${fillOrigin.x} - 50vw)`,
                          top: `calc(${fillOrigin.y} - 50vw)`,
                          width: '100vw',
                          height: '100vw',
                          background: '#f6ff7a',
                          borderRadius: '50%',
                          zIndex: 0,
                          pointerEvents: 'none',
                          transform: 'scale(0)',
                          transition: 'transform 0.3s cubic-bezier(0.42,0,1,1)',
                        },
                        '&:hover': {
                          borderColor: '#f6ff7a',
                          color: '#000',
                          '&::after': {
                            transform: 'scale(1)',
                            transition: 'transform 1.8s cubic-bezier(0,0,0.58,1)',
                          },
                          '& .MuiButton-endIcon': {
                            color: '#000',
                            zIndex: 1,
                            position: 'relative',
                          }
                        },
                        '& .MuiButton-endIcon': {
                          position: 'relative',
                          zIndex: 1,
                        }
                      }}
                    >
                      <span style={{ position: 'relative', zIndex: 1 }}>{heroView?.cta_text || 'Get In Touch'}</span>
                    </Button>
                  </Box>
                </motion.div>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6} sx={{ md: { flex: '0 0 52%', maxWidth: '52%' } }}>
              <Box sx={{ 
                marginTop: { xs: '0', sm: '0', md: '1rem' }
              }}>
                             <motion.div
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 transition={{ duration: 0.8 }}
                 className="relative"
               >
               <Box
                 sx={{
                   position: 'relative',
                   width: { xs: '100%', md: '130%' },
                   marginLeft: { xs: 0, md: '-15%' }
                 }}
               >
                 {/* Isometric AI city scene */}
                 <AICityScene />
                 </Box>
                <div className="absolute bottom-4 right-4 bg-black bg-opacity-80 p-2 rounded">
                  <div className="flex items-center">
                    <span className="text-white font-bold mr-1">Clutch</span>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span key={star} style={{ color: '#f6ff7a' }}>★</span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Clients Section with Black Background - skip layout until near viewport */}
      <section
        className="py-16 bg-black text-white"
        style={{ minHeight: '300px', contentVisibility: 'auto', containIntrinsicSize: 'auto 300px' }}
      >
        <Container 
          maxWidth={false}
          sx={{ 
            maxWidth: '1400px', 
            px: { xs: 3, sm: 4, md: 5, lg: 6 },
            margin: '0 auto'
          }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <Typography 
              variant="subtitle1" 
              component="p"
              sx={{ 
                color: '#9ca3af',
                fontStyle: 'italic',
                marginBottom: '0.5rem',
                fontSize: '1.125rem'
              }}
            >
              {clients?.label || 'Our Clients'}
            </Typography>
            <Typography 
              variant="h3" 
              component="h2"
              sx={{ 
                fontWeight: 600,
                marginBottom: '3rem',
                fontSize: { xs: '1.75rem', md: '2.25rem' }
              }}
            >
              {clients?.heading || 'Who we work with'}
          </Typography>
          </motion.div>

          <Box sx={{ position: 'relative', overflow: 'hidden' }}>
            {/* Navigation controls - visible on all devices */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 4 
            }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {[...Array(mobileTotalPages)].map((_, index) => (
                  <Box 
                    key={index}
                    sx={{ 
                      width: index === clientPage ? 24 : 8, 
                      height: 8, 
                      bgcolor: index === clientPage ? '#3b82f6' : '#9ca3af', 
                      borderRadius: index === clientPage ? 4 : '50%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => handleClientScroll(index)}
                  />
                ))}
              </Box>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%', 
                border: '1px solid #4b5563',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              onClick={() => handleClientScroll('next')}
              >
                <ArrowForwardIcon sx={{ color: 'white' }} />
              </Box>
            </Box>

            <Box 
              ref={clientsRef}
              sx={{ 
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Desktop: Show all logos */}
              <Box sx={{ 
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'space-around',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                minHeight: '120px'
              }}>
                {allClientLogos.map((client: ClientLogo, index: number) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
                    style={{ 
                      opacity: 0.8, 
                      width: '18%', 
                      maxWidth: '200px',
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    <img 
                      src={client.logo} 
                      alt={client.name} 
                      className="w-full h-16 object-contain" 
                      width="200"
                      height="64"
                      loading="lazy"
                      style={{ display: 'block' }}
                    />
                  </motion.div>
                ))}
              </Box>

              {/* Mobile/Tablet: Slider with 2 logos */}
              <Box sx={{ 
                display: { xs: 'block', md: 'none' },
                position: 'relative',
                overflow: 'hidden',
                minHeight: '120px',
                width: '100%'
              }}>
                <Box sx={{ 
                  display: 'flex',
                  transition: 'transform 0.5s ease-in-out',
                  transform: `translateX(-${(clientPage * 100) / mobileTotalPages}%)`,
                  width: `${mobileTotalPages * 100}%`
                }}>
                  {mobileClientLogos.map((page: any[], pageIndex: number) => (
                    <Box 
                      key={pageIndex}
                      sx={{ 
                        width: `${100 / mobileTotalPages}%`,
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignItems: 'center',
                        px: 2,
                        minHeight: '80px'
                      }}
                    >
                      {page.map((client: any, index: number) => (
                        <motion.div
                          key={`${pageIndex}-${index}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
                          style={{ 
                            opacity: 0.8, 
                            width: '45%', 
                            maxWidth: '200px',
                            display: 'flex',
                            justifyContent: 'center',
                            minHeight: '64px'
                          }}
                        >
                          <img 
                            src={client.logo} 
                            alt={client.name} 
                            className="w-full h-16 object-contain" 
                            width="200"
                            height="64"
                            loading="lazy"
                            style={{ minHeight: '64px', display: 'block' }}
                          />
                        </motion.div>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </section>

      {/* Services Overview Section */}
      <section
        className="pt-2 pb-16 md:pt-16 md:pb-28 bg-white"
        style={{ minHeight: '500px', contentVisibility: 'auto', containIntrinsicSize: 'auto 500px' }}
      >
        <Container 
          maxWidth={false}
          sx={{ 
            maxWidth: '1400px', 
            px: { xs: 3, sm: 4, md: 5, lg: 6 },
            margin: '0 auto'
          }}
        >
          <Grid container spacing={8}>
            <Grid item xs={12} md={5} lg={5}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                style={{ marginTop: '150px', minHeight: '200px' }}
              >
                <Typography 
                  variant="h3" 
                  component="h2" 
                  sx={{ 
                    fontWeight: 600,
                    lineHeight: 1.2,
                    marginBottom: '1.5rem',
                    fontSize: { xs: '1.75rem', md: '2.25rem' }
                  }}
                >
                  {services?.heading ? renderHighlightedText(services.heading) : (
                    <>
                      <span>BTI provides on-demand </span>
                      <Box
                        component="span"
                        sx={{
                          position: 'relative',
                          fontStyle: 'italic',
                          fontWeight: 400,
                          display: 'inline-block',
                          zIndex: 1,
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            left: -1,
                            right: -1,
                            bottom: 5,
                            height: '30%',
                            backgroundColor: 'rgba(246, 255, 122, 0.6)',
                            transform: 'rotate(-1deg)',
                            transformOrigin: 'left bottom',
                            zIndex: -1,
                            transition: 'all 0.3s ease',
                          },
                          '&:hover::after': {
                            height: '40%',
                            transform: 'rotate(-2deg)',
                          }
                        }}
                      >
                        product design
                      </Box>
                      <span> and </span>
                      <Box
                        component="span"
                        sx={{
                          position: 'relative',
                          fontStyle: 'italic',
                          fontWeight: 400,
                          display: 'inline-block',
                          zIndex: 1,
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            left: -1,
                            right: -1,
                            bottom: 5,
                            height: '30%',
                            backgroundColor: 'rgba(246, 255, 122, 0.6)',
                            transform: 'rotate(-1deg)',
                            transformOrigin: 'left bottom',
                            zIndex: -1,
                            transition: 'all 0.3s ease',
                          },
                          '&:hover::after': {
                            height: '40%',
                            transform: 'rotate(-2deg)',
                          }
                        }}
                      >
                        software engineering
                      </Box>
                      <span> and has the agility to start small or scale fast.</span>
                    </>
                  )}
                </Typography>
                <Button
                  component={Link}
                  to={services?.link_url || "/services"}
                  variant="text"
                  sx={{ 
                    color: '#000',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    marginTop: '1.5rem',
                    padding: 0,
                    borderBottom: '1px solid #000',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      borderBottom: '1px solid #f6ff7a'
                    }
                  }}
                >
                  {services?.link_text || 'Explore all services'}
                </Button>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={7} lg={7}>
              <Box
                sx={{
                  position: 'relative',
                  height: '600px',
                  width: '100%',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '300px',
                    height: '300px',
                    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.05) 50%, transparent 70%)',
                    borderRadius: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 0
                  }
                }}
              >
                {(services?.items || [
                  { name: 'UX/UI Design', icon: 'MdDesignServices' },
                  { name: 'Quality Assurance (QA)', icon: 'MdBugReport' },
                  { name: 'DevOps', icon: 'TbSettings' },
                  { name: 'Mobile Development', icon: 'MdPhoneAndroid' },
                  { name: 'Product Strategy', icon: 'HiLightBulb' },
                  { name: 'AI & Machine Learning', icon: 'TbRobot' },
                  { name: 'Web Development', icon: 'MdCode' },
                  { name: 'Blockchain Development', icon: 'TbLink' }
                ]).map((service: any, index: number) => {
                  // Abstract positioning - create organic, non-grid layout
                  const positions = [
                    { top: '10%', left: '5%', rotate: -5 },
                    { top: '25%', right: '10%', rotate: 8 },
                    { top: '45%', left: '15%', rotate: -12 },
                    { top: '60%', right: '5%', rotate: 15 },
                    { top: '15%', left: '50%', rotate: -8 },
                    { top: '35%', right: '25%', rotate: 10 },
                    { top: '70%', left: '8%', rotate: -15 },
                    { top: '80%', right: '15%', rotate: 6 }
                  ];
                  
                  const position = positions[index] || { top: '50%', left: '50%', rotate: 0 };
                  
                  return (
                    <motion.div
                      key={index}
                      data-service-index={index}
                      initial={{ 
                        opacity: 0, 
                        scale: 0.3,
                        rotate: position.rotate - 20,
                        x: Math.random() * 100 - 50,
                        y: Math.random() * 100 - 50
                      }}
                      animate={{ 
                        opacity: 1, 
                        scale: 1,
                        rotate: position.rotate,
                        x: 0,
                        y: 0
                      }}
                      transition={{ 
                        duration: 0.8, 
                        delay: 0.2 + (index * 0.15),
                        type: "spring",
                        stiffness: 60,
                        damping: 15
                      }}
                      whileHover={{ 
                        scale: 1.15,
                        rotate: position.rotate + (Math.random() * 10 - 5),
                        y: -10,
                        zIndex: 20,
                        transition: { duration: 0.3, ease: "easeOut" }
                      }}
                      onHoverStart={() => {
                        // Ensure the hovered card is on top on mobile
                        const element = document.querySelector(`[data-service-index="${index}"]`) as HTMLElement;
                        if (element) {
                          element.style.zIndex = '20';
                        }
                      }}
                      onHoverEnd={() => {
                        // Reset z-index when hover ends
                        const element = document.querySelector(`[data-service-index="${index}"]`) as HTMLElement;
                        if (element) {
                          element.style.zIndex = '1';
                        }
                      }}
                      onTouchStart={() => {
                        // Touch support for mobile devices
                        const element = document.querySelector(`[data-service-index="${index}"]`) as HTMLElement;
                        if (element) {
                          element.style.zIndex = '20';
                        }
                      }}
                      onTouchEnd={() => {
                        // Reset z-index when touch ends
                        setTimeout(() => {
                          const element = document.querySelector(`[data-service-index="${index}"]`) as HTMLElement;
                          if (element) {
                            element.style.zIndex = '1';
                          }
                        }, 1000); // Delay to allow user to see the effect
                      }}
                      style={{
                        position: 'absolute',
                        top: position.top,
                        left: position.left,
                        right: position.right,
                        zIndex: 1
                      }}
                    >
                      <Box
                        sx={{
                          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          borderRadius: '24px',
                          padding: '1.25rem 1.5rem',
                          cursor: 'pointer',
                          position: 'relative',
                          overflow: 'hidden',
                          backdropFilter: 'blur(20px)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          minWidth: '180px',
                          maxWidth: '220px',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `linear-gradient(135deg, 
                              rgba(99, 102, 241, ${0.05 + index * 0.02}) 0%, 
                              rgba(236, 72, 153, ${0.03 + index * 0.015}) 100%)`,
                            opacity: 0,
                            transition: 'opacity 0.4s ease',
                            borderRadius: '24px'
                          },
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: '-50%',
                            left: '-50%',
                            width: '200%',
                            height: '200%',
                            background: `conic-gradient(from ${index * 45}deg, 
                              transparent, 
                              rgba(99, 102, 241, 0.1), 
                              rgba(236, 72, 153, 0.08),
                              transparent)`,
                            opacity: 0,
                            transition: 'opacity 0.4s ease',
                            animation: 'none'
                          },
                          '&:hover': {
                            transform: 'translateY(-12px) scale(1.05)',
                            zIndex: 20,
                            '& .service-icon': {
                              background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                              transform: 'scale(1.2) rotate(10deg)',
                              boxShadow: '0 8px 25px rgba(99, 102, 241, 0.3)',
                              '& svg': {
                                color: 'black !important',
                                WebkitTextFillColor: 'black !important',
                                background: 'none !important',
                                backgroundClip: 'unset !important',
                                WebkitBackgroundClip: 'unset !important'
                              }
                            },
                            '& .service-text': {
                              background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              fontWeight: 700,
                              transform: 'translateX(4px)'
                            }
                          }
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            position: 'relative',
                            zIndex: 2
                          }}
                        >
                          <Box
                            className="service-icon"
                            sx={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                              flexShrink: 0,
                              position: 'relative',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                inset: '2px',
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                zIndex: 1
                              }
                            }}
                          >
                            <DynamicIcon 
                              iconName={typeof service === 'object' ? service.icon : 'TbSettings'}
                              style={{
                                fontSize: '16px',
                                background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                position: 'relative',
                                zIndex: 2
                              }}
                            />
                          </Box>
                          <Typography 
                            variant="body1" 
                            className="service-text"
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '1rem',
                              color: '#475569',
                              margin: 0,
                              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                              letterSpacing: '-0.025em',
                              lineHeight: 1.3
                            }}
                          >
                            {typeof service === 'object' ? service.name : service}
                      </Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* Capabilities Section - lazy loaded to reduce initial bundle */}
      <Suspense fallback={<div className="min-h-[400px]" aria-hidden />}>
        <Capabilities />
      </Suspense>

      {/* Reviews Section */}
      <section ref={reviewsSectionRef} className="py-20 bg-[#111111] relative overflow-hidden" style={{ minHeight: '600px' }}>
        {/* Gradient orbs */}
        <Box
          sx={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8787 100%)',
            borderRadius: '50%',
            filter: 'blur(80px)',
            top: '5%',
            right: '-5%',
            opacity: 0.2,
            transform: (theme) => getTransform(window.innerWidth * 0.95, window.innerHeight * 0.05, 1, -15),
            transition: 'transform 0.3s ease-out',
            '&:hover': {
              opacity: 0.25
            }
          }}
        />

        {/* Second gradient orb */}
        <Box
          sx={{
            position: 'absolute',
            width: '250px',
            height: '250px',
            background: 'linear-gradient(135deg, #4ECDC4 0%, #45B7AF 100%)',
            borderRadius: '50%',
            filter: 'blur(80px)',
            bottom: '10%',
            left: '-5%',
            opacity: 0.2,
            transform: 'rotate(15deg)',
            transition: 'transform 0.3s ease-out',
            '&:hover': {
              transform: 'rotate(15deg) scale(1.1)',
            }
          }}
        />

        {/* Purple gradient orb */}
        <Box
          sx={{
            position: 'absolute',
            width: '200px',
            height: '200px',
            background: 'linear-gradient(135deg, #9C27B0 0%, #673AB7 100%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            top: '40%',
            right: '20%',
            opacity: 0.15,
            transform: 'rotate(30deg)',
            transition: 'transform 0.3s ease-out',
          }}
        />

        {/* Orange gradient blob */}
        <Box
          sx={{
            position: 'absolute',
            width: '200px',
            height: '200px',
            background: 'linear-gradient(135deg, #FF9966 0%, #FF5E62 100%)',
            borderRadius: '63% 37% 70% 30% / 47% 63% 37% 53%',
            filter: 'blur(40px)',
            top: '20%',
            left: '15%',
            opacity: 0.15,
            transform: (theme) => getTransform(window.innerWidth * 0.15, window.innerHeight * 0.2, 1, 0),
            transition: 'transform 0.2s ease-out',
            animation: 'morphBlob 20s linear infinite',
            '@keyframes morphBlob': {
              '0%, 100%': {
                borderRadius: '63% 37% 70% 30% / 47% 63% 37% 53%',
              },
              '50%': {
                borderRadius: '37% 63% 30% 70% / 63% 47% 53% 37%',
              }
            }
          }}
        />

        {/* Colored circles with enhanced mouse interaction */}
        {[
          { size: 25, color: '#4ECDC4', top: '30%', left: '10%', delay: 8, rotation: 15 },
          { size: 15, color: '#FFD93D', top: '25%', left: '25%', delay: 6, rotation: -20 },
          { size: 20, color: '#FF6B6B', bottom: '30%', left: '20%', delay: 7, rotation: 45 },
          { size: 18, color: '#9C27B0', top: '15%', right: '25%', delay: 9, rotation: 30 },
          { size: 22, color: '#2196F3', bottom: '25%', right: '15%', delay: 7.5, rotation: -15 },
          { size: 12, color: '#4CAF50', top: '45%', left: '35%', delay: 6.5, rotation: 60 }
        ].map((circle, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              width: `${circle.size}px`,
              height: `${circle.size}px`,
              borderRadius: '50%',
              background: circle.color,
              top: circle.top,
              left: circle.left,
              right: circle.right,
              bottom: circle.bottom,
              opacity: 0.7,
              transform: (theme) => getTransform(
                window.innerWidth * (parseInt(circle.left as string) || (100 - parseInt(circle.right as string))) / 100,
                window.innerHeight * (parseInt(circle.top as string) || (100 - parseInt(circle.bottom as string))) / 100,
                1,
                circle.rotation
              ),
              transition: 'transform 0.15s ease-out, opacity 0.3s ease, box-shadow 0.3s ease',
              animation: `floatSlow ${circle.delay}s ease-in-out infinite`,
              zIndex: 1,
              boxShadow: '0 0 10px rgba(255, 255, 255, 0.1)',
              '&:hover': {
                opacity: 0.9,
                boxShadow: '0 0 20px rgba(255, 255, 255, 0.2)',
              }
            }}
          />
        ))}

        {/* Small dots with enhanced mouse interaction */}
        {[...Array(12)].map((_, i) => (
          <Box
            key={i}
              sx={{ 
              position: 'absolute',
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.5)',
              top: `${15 + (i * 6)}%`,
              right: `${5 + (i * 3)}%`,
              opacity: 0.5,
              transform: (theme) => getTransform(
                window.innerWidth * (95 - i * 3) / 100,
                window.innerHeight * (15 + i * 6) / 100,
                1,
                i * 30
              ),
              transition: 'transform 0.15s ease-out, opacity 0.3s ease',
              animation: `twinkle ${3 + i}s ease-in-out infinite`,
              '@keyframes twinkle': {
                '0%, 100%': { opacity: 0.5, transform: 'scale(1)' },
                '50%': { opacity: 0.9, transform: 'scale(1.5)' }
              },
              zIndex: 1,
                '&:hover': {
                opacity: 1,
                transform: 'scale(2)'
              }
            }}
          />
        ))}

        {/* Triangles with enhanced mouse interaction */}
        {[
          { size: 20, color: 'rgba(78, 205, 196, 0.4)', top: '40%', right: '30%', rotation: 15 },
          { size: 15, color: 'rgba(255, 217, 61, 0.4)', bottom: '35%', right: '15%', rotation: -25 },
          { size: 25, color: 'rgba(156, 39, 176, 0.4)', top: '20%', left: '40%', rotation: 45 }
        ].map((triangle, index) => (
          <Box
            key={`triangle-${index}`}
            sx={{
              position: 'absolute',
              width: 0,
              height: 0,
              borderLeft: `${triangle.size}px solid transparent`,
              borderRight: `${triangle.size}px solid transparent`,
              borderBottom: `${triangle.size * 1.5}px solid ${triangle.color}`,
              top: triangle.top,
              bottom: triangle.bottom,
              right: triangle.right,
              left: triangle.left,
              transform: (theme) => getTransform(
                window.innerWidth * (parseInt(triangle.left as string) || (100 - parseInt(triangle.right as string))) / 100,
                window.innerHeight * (parseInt(triangle.top as string) || (100 - parseInt(triangle.bottom as string))) / 100,
                1,
                triangle.rotation
              ),
              transition: 'transform 0.15s ease-out, opacity 0.3s ease, filter 0.3s ease',
              animation: `floatSlow ${7 + index}s ease-in-out infinite`,
              zIndex: 1,
              filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.1))',
              '&:hover': {
                opacity: 0.8,
                filter: 'drop-shadow(0 0 15px rgba(255, 255, 255, 0.2))',
              }
            }}
          />
        ))}

        {/* Paper Plane with enhanced mouse interaction */}
        <Box
          sx={{
            position: 'absolute',
            width: '40px',
            height: '40px',
            bottom: '20%',
            right: '20%',
            opacity: 0.7,
            transform: (theme) => getTransform(
              window.innerWidth * 0.8,
              window.innerHeight * 0.8,
              1,
              45
            ),
            transition: 'transform 0.2s ease-out, opacity 0.3s ease',
            animation: 'flyAround 10s linear infinite',
            zIndex: 1,
            '&:hover': {
              opacity: 1,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '0',
              left: '0',
              borderLeft: '20px solid transparent',
              borderRight: '20px solid transparent',
              borderBottom: '40px solid rgba(255, 255, 255, 0.3)',
              transform: 'rotate(45deg)',
            }
          }}
        />

        {/* Main content container */}
        <Container maxWidth={false} sx={{ maxWidth: '1400px', position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '2rem', md: '2.5rem' },
                marginBottom: '1rem',
                color: '#fff'
              }}
            >
              {testimonialsData?.title_line_1 || 'Your feedback and review'}
            </Typography>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: '#FF6B6B'
              }}
            >
              {testimonialsData?.title_line_2 || 'important to us!'}
            </Typography>
          </motion.div>

          {/* Desktop: Original 3-cards-per-page slider */}
          <Box sx={{ position: 'relative', display: { xs: 'none', md: 'block' } }}>
            {/* Navigation Arrows */}
            <Box
              onClick={() => handleReviewScroll('prev')}
              sx={{
                position: 'absolute',
                left: '-60px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                zIndex: 3,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  transform: 'translateY(-50%) scale(1.1)',
                }
              }}
            >
              <ArrowForwardIcon sx={{ transform: 'rotate(180deg)', color: 'white' }} />
            </Box>

            <Box
              onClick={() => handleReviewScroll('next')}
              sx={{
                position: 'absolute',
                right: '-60px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                zIndex: 3,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  transform: 'translateY(-50%) scale(1.1)',
                }
              }}
            >
              <ArrowForwardIcon sx={{ color: 'white' }} />
            </Box>

            {/* Desktop Reviews Slider */}
            <Box
              sx={{
                overflow: 'hidden',
                position: 'relative',
                width: '100%',
                minHeight: '400px'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  transition: 'transform 0.5s ease-in-out',
                  transform: `translateX(-${currentReview * 100}%)`,
                  width: '100%'
                }}
              >
                <Grid container spacing={4} sx={{ flexWrap: 'nowrap', width: '100%' }}>
                  {testimonials.map((testimonial: Testimonial, index: number) => (
                    <Grid 
                      item 
                      key={index} 
                      sx={{ 
                        flex: '0 0 33.333%',
                        width: '33.333%',
                        pl: index === 0 ? 0 : 2,
                        pr: index === testimonials.length - 1 ? 0 : 2
                      }}
                    >
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <Box
                          sx={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '24px',
                            p: 4,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              transition: 'all 0.3s ease',
                              background: 'rgba(255, 255, 255, 0.08)',
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                            {[1, 2, 3, 4, 5].map((star) => (
                              <span key={star} style={{ color: '#FFD700', fontSize: '20px' }}>★</span>
                            ))}
                          </Box>
                          <Typography 
                            variant="h5" component="h3" 
                            sx={{ 
                              mb: 3,
                              color: '#fff',
                              fontSize: '1.5rem',
                              fontWeight: 600
                            }}
                          >
                            Excellent service!
                          </Typography>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              mb: 4,
                              color: 'rgba(255, 255, 255, 0.8)',
                              fontSize: '1rem',
                              lineHeight: 1.7,
                              flex: 1
                            }}
                          >
                            "{testimonial.quote}"
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                overflow: 'hidden',
                                mr: 2
                              }}
                            >
                              <img 
                                src={`https://i.pravatar.cc/150?u=${testimonial.author}`}
                                alt={testimonial.author}
                                width="48"
                                height="48"
                                loading="lazy"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              />
                            </Box>
                            <Box>
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: '#fff',
                                  fontSize: '1rem'
                                }}
                              >
                                {testimonial.author}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'rgba(255, 255, 255, 0.6)',
                                  fontSize: '0.875rem'
                                }}
                              >
                                {testimonial.position}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* Desktop Pagination Dots */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 4 }}>
              {[...Array(totalReviews)].map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: index === currentReview ? '24px' : '8px',
                    height: '8px',
                    borderRadius: index === currentReview ? '4px' : '50%',
                    backgroundColor: index === currentReview ? '#FF6B6B' : 'rgba(255, 255, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => setCurrentReview(index)}
                />
              ))}
            </Box>
          </Box>

          {/* Mobile/Tablet: Single card slider */}
          <Box sx={{ position: 'relative', display: { xs: 'block', md: 'none' } }}>
            {/* Mobile Navigation Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Box
                onClick={() => handleMobileReviewScroll('prev')}
                sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    transform: 'scale(1.1)',
                  }
                }}
              >
                <ArrowForwardIcon sx={{ transform: 'rotate(180deg)', color: 'white', fontSize: '20px' }} />
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                {[...Array(totalMobileReviewPages)].map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: index === currentMobileReviewPage ? '20px' : '6px',
                      height: '6px',
                      borderRadius: index === currentMobileReviewPage ? '3px' : '50%',
                      backgroundColor: index === currentMobileReviewPage ? '#FF6B6B' : 'rgba(255, 255, 255, 0.3)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleMobileReviewScroll(index)}
                  />
                ))}
              </Box>

              <Box
                onClick={() => handleMobileReviewScroll('next')}
                sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    transform: 'scale(1.1)',
                  }
                }}
              >
                <ArrowForwardIcon sx={{ color: 'white', fontSize: '20px' }} />
              </Box>
            </Box>

            {/* Mobile Reviews Slider */}
            <Box
              sx={{
                overflow: 'hidden',
                position: 'relative',
                width: '100%',
                minHeight: '400px'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  transition: 'transform 0.5s ease-in-out',
                  transform: `translateX(-${(currentMobileReviewPage * 100) / totalMobileReviewPages}%)`,
                  width: `${totalMobileReviewPages * 100}%`
                }}
              >
                {testimonials.map((testimonial: Testimonial, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      width: `${100 / totalMobileReviewPages}%`,
                      px: 2
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                    >
                      <Box
                        sx={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: '24px',
                          p: 4,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            transition: 'all 0.3s ease',
                            background: 'rgba(255, 255, 255, 0.08)',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span key={star} style={{ color: '#FFD700', fontSize: '20px' }}>★</span>
                          ))}
                        </Box>
                        <Typography 
                          variant="h5" component="h3" 
                          sx={{ 
                            mb: 3,
                            color: '#fff',
                            fontSize: '1.5rem',
                            fontWeight: 600
                          }}
                        >
                          Excellent service!
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            mb: 4,
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontSize: '1rem',
                            lineHeight: 1.7,
                            flex: 1
                          }}
                        >
                          "{testimonial.quote}"
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '50%',
                              overflow: 'hidden',
                              mr: 2
                            }}
                          >
                            <img 
                              src={`https://i.pravatar.cc/150?u=${testimonial.author}`}
                              alt={testimonial.author}
                              width="48"
                              height="48"
                              loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                          </Box>
                          <Box>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                fontWeight: 600,
                                color: '#fff',
                                fontSize: '1rem'
                              }}
                            >
                              {testimonial.author}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'rgba(255, 255, 255, 0.6)',
                                fontSize: '0.875rem'
                              }}
                            >
                              {testimonial.position}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </motion.div>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Button
              component={Link}
              to="/reviews"
              variant="outlined"
              sx={{ 
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: '#fff',
                borderRadius: '9999px',
                padding: '0.75rem 2rem',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#FF6B6B',
                  backgroundColor: 'rgba(255, 107, 107, 0.1)'
                }
              }}
            >
              View All Reviews
            </Button>
          </Box>
        </Container>
      </section>

      {/* Modern Wave Divider */}
      <Box 
        sx={{ 
          position: 'relative',
          height: '200px',
          overflow: 'hidden',
          background: 'linear-gradient(to bottom, #111111 50%, #ffffff 50%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            height: '100px',
            background: '#ffffff',
            transform: 'skewY(-3deg)',
            transformOrigin: 'center',
            boxShadow: '0px -10px 30px rgba(0, 0, 0, 0.1)',
            zIndex: 1
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            left: '-5%',
            right: '-5%',
            top: '45%',
            height: '100px',
            background: '#ffffff',
            transform: 'skewY(5deg)',
            transformOrigin: 'center',
            boxShadow: '0px -10px 30px rgba(0, 0, 0, 0.1)',
            zIndex: 2
          }
        }}
      >
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            zIndex: 3,
            '&::before': {
              content: '""',
              position: 'absolute',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: 'rgba(246, 255, 122, 0.3)',
              top: '40%',
              left: '10%',
              animation: 'float 6s ease-in-out infinite',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              width: '15px',
              height: '15px',
              borderRadius: '50%',
              background: 'rgba(78, 205, 196, 0.3)',
              top: '60%',
              right: '15%',
              animation: 'float 8s ease-in-out infinite',
            },
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0)',
              },
              '50%': {
                transform: 'translateY(-20px)',
              },
            },
          }}
        >
          {/* Additional floating elements */}
          {[...Array(5)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: `${10 + i * 2}px`,
                height: `${10 + i * 2}px`,
                borderRadius: '50%',
                background: `rgba(${255 - i * 30}, ${107 + i * 20}, ${107 + i * 20}, 0.2)`,
                top: `${30 + i * 10}%`,
                left: `${20 + i * 15}%`,
                animation: `float ${5 + i}s ease-in-out infinite ${i}s`,
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Get Started Section - skip layout until near viewport */}
      <section className="py-20" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 600px' }}>
        <Container maxWidth={false} sx={{ maxWidth: '1400px' }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
                fontWeight: 600,
                mb: 2,
                color: '#111111',
                textAlign: 'center'
              }}
            >
              {getStarted?.heading || 'Get Started in as Little as Two Weeks'}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(17, 17, 17, 0.7)',
                fontSize: { xs: '1rem', md: '1.125rem' },
                maxWidth: '800px',
                margin: '0 auto'
              }}
            >
              {getStarted?.description || 'We provide a free and guaranteed estimate for cost and timeline with every engagement.'}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Staff Augmentation Card */}
            <Grid item xs={12} md={4}>
                            <Box
                onClick={() => navigate('/services/staff-augmentation')}
                sx={{
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.05) 0%, rgba(78, 205, 196, 0.1) 100%)',
                  borderRadius: '24px',
                  p: 4,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.2) 0%, transparent 100%)',
                    opacity: 0,
                    transition: 'opacity 0.4s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    '&::before': {
                      opacity: 1,
                    },
                    '& .icon-container': {
                      transform: 'scale(1.1) rotate(5deg)',
                      background: 'rgba(78, 205, 196, 0.15)',
                    }
                  }
                }}
              >
                <Box
                  className="icon-container"
                  sx={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '20px',
                    background: 'rgba(78, 205, 196, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: '-2px',
                      borderRadius: '22px',
                      border: '2px solid transparent',
                      background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.5), transparent) border-box',
                      WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
                      WebkitMaskComposite: 'xor',
                      maskComposite: 'exclude',
                    }
                  }}
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#4ECDC4"/>
                  </svg>
                </Box>
                <Typography 
                  variant="h5" component="h3" 
                  sx={{ 
                    fontWeight: 600,
                    mb: 2,
                    fontSize: '1.5rem',
                    color: '#111111',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  Staff Augmentation
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'rgba(17, 17, 17, 0.7)',
                    lineHeight: 1.7,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  Add senior engineers to your team seamlessly for less than the cost of US employees. We staff individuals as well as teams of 50+ people, plus project managers, DevOps, QA, and designers.
                </Typography>
              </Box>
            </Grid>

            {/* Product Studio Card */}
            <Grid item xs={12} md={4}>
              <Box
                onClick={() => navigate('/services/product-studio')}
                sx={{
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.05) 0%, rgba(255, 107, 107, 0.1) 100%)',
                  borderRadius: '24px',
                  p: 4,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.2) 0%, transparent 100%)',
                    opacity: 0,
                    transition: 'opacity 0.4s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    '&::before': {
                      opacity: 1,
                    },
                    '& .icon-container': {
                      transform: 'scale(1.1) rotate(-5deg)',
                      background: 'rgba(255, 107, 107, 0.15)',
                    }
                  }
                }}
              >
                <Box
                  className="icon-container"
                  sx={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '20px',
                    background: 'rgba(255, 107, 107, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: '-2px',
                      borderRadius: '22px',
                      border: '2px solid transparent',
                      background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.5), transparent) border-box',
                      WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
                      WebkitMaskComposite: 'xor',
                      maskComposite: 'exclude',
                    }
                  }}
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3H11V11H3V3ZM3 13H11V21H3V13ZM13 3H21V11H13V3ZM13 13H21V21H13V13Z" fill="#FF6B6B"/>
                  </svg>
                </Box>
                <Typography 
                  variant="h5" component="h3" 
                  sx={{ 
                    fontWeight: 600,
                    mb: 2,
                    fontSize: '1.5rem',
                    color: '#111111',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  Product Studio
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'rgba(17, 17, 17, 0.7)',
                    lineHeight: 1.7,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  Build a high-quality, user-centric product or feature from scratch, fast and on budget. We staff embedded product teams with PMs, UX/UI designers, frontend, backend, data engineers, and QA.
                </Typography>
              </Box>
            </Grid>

            {/* IT Outsourcing Card */}
            <Grid item xs={12} md={4}>
              <Box
                onClick={() => navigate('/services/it-outsourcing')}
                sx={{
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(99, 102, 241, 0.1) 100%)',
                  borderRadius: '24px',
                  p: 4,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, transparent 100%)',
                    opacity: 0,
                    transition: 'opacity 0.4s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    '&::before': { opacity: 1 },
                    '& .icon-container': { transform: 'scale(1.1) rotate(5deg)', background: 'rgba(99, 102, 241, 0.15)' },
                  }
                }}
              >
                <Box className="icon-container" sx={{ width: '72px', height: '72px', borderRadius: '20px', background: 'rgba(99, 102, 241, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', position: 'relative', '&::after': { content: '""', position: 'absolute', inset: '-2px', borderRadius: '22px', border: '2px solid transparent', background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.5), transparent) border-box', WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude' } }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6H16V4C16 2.9 15.1 2 14 2H10C8.9 2 8 2.9 8 4V6H4C2.9 6 2 6.9 2 8V20C2 21.1 2.9 22 4 22H20C21.1 22 22 21.1 22 20V8C22 6.9 21.1 6 20 6ZM10 4H14V6H10V4ZM20 20H4V8H20V20Z" fill="#6366F1"/>
                    <path d="M13 10H11V13H8V15H11V18H13V15H16V13H13V10Z" fill="#6366F1"/>
                  </svg>
                </Box>
                <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 2, fontSize: '1.5rem', color: '#111111', position: 'relative', zIndex: 1 }}>
                  IT Outsourcing
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(17, 17, 17, 0.7)', lineHeight: 1.7, position: 'relative', zIndex: 1 }}>
                  Scale your technology capability without full-time hiring overhead. Expert IT outsourcing, extended development teams, and strategic IT consulting — engineers in 2 weeks, not months.
                </Typography>
              </Box>
            </Grid>

            {/* Custom Software Development Card */}
            <Grid item xs={12} md={4}>
              <Box
                onClick={() => navigate('/services/custom-software-development')}
                sx={{
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.05) 0%, rgba(236, 72, 153, 0.1) 100%)',
                  borderRadius: '24px',
                  p: 4,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, transparent 100%)',
                    opacity: 0,
                    transition: 'opacity 0.4s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    '&::before': { opacity: 1 },
                    '& .icon-container': { transform: 'scale(1.1) rotate(5deg)', background: 'rgba(236, 72, 153, 0.15)' },
                  }
                }}
              >
                <Box className="icon-container" sx={{ width: '72px', height: '72px', borderRadius: '20px', background: 'rgba(236, 72, 153, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', position: 'relative', '&::after': { content: '""', position: 'absolute', inset: '-2px', borderRadius: '22px', border: '2px solid transparent', background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.5), transparent) border-box', WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude' } }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z" fill="#EC4899"/>
                  </svg>
                </Box>
                <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 2, fontSize: '1.5rem', color: '#111111', position: 'relative', zIndex: 1 }}>
                  Custom Software Dev
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(17, 17, 17, 0.7)', lineHeight: 1.7, position: 'relative', zIndex: 1 }}>
                  Web apps, mobile apps, SaaS platforms, and AI-powered software built to your exact specs. 1,000+ projects delivered across healthcare, finance, and e-commerce.
                </Typography>
              </Box>
            </Grid>

            {/* GoHighLevel Expert Card */}
            <Grid item xs={12} md={4}>
              <Box
                onClick={() => navigate('/services/gohighlevel-expert')}
                sx={{
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, rgba(245, 158, 66, 0.05) 0%, rgba(245, 158, 66, 0.1) 100%)',
                  borderRadius: '24px',
                  p: 4,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'linear-gradient(135deg, rgba(245, 158, 66, 0.2) 0%, transparent 100%)',
                    opacity: 0,
                    transition: 'opacity 0.4s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    '&::before': { opacity: 1 },
                    '& .icon-container': { transform: 'scale(1.1) rotate(5deg)', background: 'rgba(245, 158, 66, 0.15)' },
                  }
                }}
              >
                <Box className="icon-container" sx={{ width: '72px', height: '72px', borderRadius: '20px', background: 'rgba(245, 158, 66, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', position: 'relative', '&::after': { content: '""', position: 'absolute', inset: '-2px', borderRadius: '22px', border: '2px solid transparent', background: 'linear-gradient(135deg, rgba(245, 158, 66, 0.5), transparent) border-box', WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude' } }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19.428 15.428a2 2 0 00-1.022-.54l-2.46-.492a6 6 0 01-4.083-3.414l-.205-.432a2 2 0 00-1.83-1.176H8a2 2 0 00-1.768 1.073L4.6 13.5H3a1 1 0 000 2h.6l1.9 3.1a1 1 0 001.7-1.04L5.9 15.5h2.1a2 2 0 001.768-1.073l.132-.265a6 6 0 004.083 3.414l2.46.492a2 2 0 001.022-.54l1.157-1.157a1 1 0 10-1.414-1.414l-.78.78z" fill="#F59E42"/>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" fill="#F59E42"/>
                  </svg>
                </Box>
                <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 2, fontSize: '1.5rem', color: '#111111', position: 'relative', zIndex: 1 }}>
                  GoHighLevel Expert
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(17, 17, 17, 0.7)', lineHeight: 1.7, position: 'relative', zIndex: 1 }}>
                  Dedicated GHL expert team for CRM setup, automation workflows, funnel builds, and white-label SaaS Mode. Fixed-price proposals in 24 hours.
                </Typography>
              </Box>
            </Grid>

            {/* Project Sprints Card */}
            <Grid item xs={12} md={4}>
              <Box
                onClick={() => navigate('/services/project-sprints')}
                sx={{
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, rgba(255, 217, 61, 0.05) 0%, rgba(255, 217, 61, 0.1) 100%)',
                  borderRadius: '24px',
                  p: 4,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(255, 217, 61, 0.2) 0%, transparent 100%)',
                    opacity: 0,
                    transition: 'opacity 0.4s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    '&::before': {
                      opacity: 1,
                    },
                    '& .icon-container': {
                      transform: 'scale(1.1) rotate(10deg)',
                      background: 'rgba(255, 217, 61, 0.15)',
                    }
                  }
                }}
              >
                <Box
                  className="icon-container"
                  sx={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '20px',
                    background: 'rgba(255, 217, 61, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: '-2px',
                      borderRadius: '22px',
                      border: '2px solid transparent',
                      background: 'linear-gradient(135deg, rgba(255, 217, 61, 0.5), transparent) border-box',
                      WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
                      WebkitMaskComposite: 'xor',
                      maskComposite: 'exclude',
                    }
                  }}
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.5 8H12V13L16.28 15.54L17 14.33L13.5 12.25V8ZM13 3C8.03 3 4 7.03 4 12H1L4.89 15.89L4.96 16.03L9 12H6C6 8.13 9.13 5 13 5C16.87 5 20 8.13 20 12C20 15.87 16.87 19 13 19C11.07 19 9.32 18.21 8.06 16.94L6.64 18.36C8.27 19.99 10.51 21 13 21C17.97 21 22 16.97 22 12C22 7.03 17.97 3 13 3Z" fill="#FFD93D"/>
                  </svg>
                </Box>
                <Typography 
                  variant="h5" component="h3" 
                  sx={{ 
                    fontWeight: 600,
                    mb: 2,
                    fontSize: '1.5rem',
                    color: '#111111',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  Project Sprints
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'rgba(17, 17, 17, 0.7)',
                    lineHeight: 1.7,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  Audit data, code or UX/UI. Train and optimize an AI/ML model. Scope a migration or upgrade. Build a proof of concept or design a prototype. We offer many short-term, shovel-ready projects.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Steps to Get Started Section - lazy loaded to reduce initial bundle */}
          <Suspense fallback={<div className="min-h-[200px]" aria-hidden />}>
            <FlowchartSteps />
          </Suspense>
        </Container>
      </section>

      

      {/* Talent Community Section - Never Miss an Update! - skip layout until near viewport */}
      <section
        className="py-20 flex justify-center items-center"
        style={{
          backgroundColor: 'rgb(25 25 25 / var(--tw-bg-opacity, 1))',
          contentVisibility: 'auto',
          containIntrinsicSize: 'auto 400px',
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: '1200px' }}>
          <Box className="rounded-2xl p-10 flex flex-col md:flex-row items-center md:items-center gap-8 relative overflow-hidden" style={{ backgroundColor: 'rgb(25 25 25 / var(--tw-bg-opacity, 1))' }}>
            <Box className="flex-1 flex flex-col items-center md:items-start text-center md:text-left">
              <Typography variant="h3" sx={{ fontWeight: 800, color: '#fff', mb: 2, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                {newsletter?.heading || 'Never Miss an Update!'}
              </Typography>
              <Typography variant="body1" sx={{ color: '#d1d5db', mb: 4, fontSize: '1.15rem', maxWidth: '500px' }}>
                {newsletter?.description || 'Join our community of insiders and never miss out on exciting news, product launches, and more.'}
              </Typography>
              <Button
                variant="contained"
                onClick={handleNewsletterOpen}
                sx={{
                  backgroundColor: '#f6ff7a',
                  color: '#111',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  px: 6,
                  py: 2,
                  borderRadius: '9999px',
                  boxShadow: '0 4px 16px 0 rgba(246,255,122,0.10)',
                  '&:hover': { backgroundColor: '#e6ef6c' }
                }}
              >
                {newsletter?.cta_text || 'Join the community'}
              </Button>
            </Box>
            {/* Geometric SVG accent */}
            <Box className="hidden md:block absolute right-0 top-0 h-full w-1/3 pointer-events-none">
              <svg width="100%" height="100%" viewBox="0 0 300 300" fill="none">
                <rect x="50" y="50" width="200" height="200" rx="32" stroke="#f6ff7a" strokeWidth="3" fill="none"/>
                <rect x="80" y="80" width="140" height="140" rx="24" stroke="#f6ff7a" strokeWidth="2" fill="none"/>
                <rect x="110" y="110" width="80" height="80" rx="16" stroke="#f6ff7a" strokeWidth="1.5" fill="none"/>
              </svg>
            </Box>
          </Box>
        </Container>
      </section>

      {/* Newsletter Subscription Popup */}
      <Dialog
        open={newsletterOpen}
        onClose={handleNewsletterClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            pb: 1,
            position: 'relative'
          }}
        >
          <IconButton
            onClick={handleNewsletterClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'rgba(0, 0, 0, 0.6)',
              '&:hover': {
                color: 'rgba(0, 0, 0, 0.8)',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#111111',
              fontSize: { xs: '1.5rem', md: '2rem' },
              mb: 1
            }}
          >
            Join Our Community! 🎉
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(17, 17, 17, 0.7)',
              fontSize: '1rem',
              maxWidth: '400px',
              mx: 'auto'
            }}
          >
            Stay updated with the latest news, product launches, and exclusive insights from our team.
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ px: 4, pb: 2 }}>
          <form onSubmit={handleNewsletterSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email address"
              variant="outlined"
              required
              sx={{
                mt: 2,
                mb: 3,
                ...textFieldStyle
              }}
            />
            
            <DialogActions sx={{ px: 0, pb: 0 }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: '#f6ff7a',
                  color: '#111111',
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  boxShadow: '0 4px 16px rgba(246, 255, 122, 0.3)',
                  '&:hover': {
                    backgroundColor: '#e6ef6c',
                    boxShadow: '0 6px 20px rgba(246, 255, 122, 0.4)'
                  }
                }}
              >
                Subscribe to Newsletter
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '12px',
            fontWeight: 500
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default NewHome; 